import { Sim, ToolId, SeedType, SEED_TYPES, BUILDING_STAGES, totalBuildingCost, BuildingType } from '../../game/simulation';
import { ICON } from '../icons';

/**
 * Bottom-center hotbar — Stardew-style. Slots:
 *
 *   [1: Create area] [2: Gardening] [3: Axe] [4: Pickaxe] [5: Build]
 *
 * "Hands" was removed: the mouse pointer IS the hands tool — having a
 * dedicated slot for "do nothing in particular" was redundant noise. Any
 * non-tool click (close gardening popover, finish a placement) sets the
 * sim tool back to `hands` internally.
 *
 * Two slots open POPOVERS above the hotbar instead of selecting a tool
 * directly:
 *   - Gardening → tools row (Hoe / Water / Plant / Sickle). When Plant is
 *     active, a SECOND popover (the seed picker) stacks above the
 *     gardening one so the player can see both at the same time.
 *   - Build → list of buildings the player can construct, with cost and
 *     a "Build this" button that enters placement mode.
 *
 * Click the same slot again, hit Esc, or pick a different slot to close
 * the popover. Build's popover also closes the moment the player builds.
 */
type SlotKind =
  | { kind: 'plot' }
  | { kind: 'gardening' }
  | { kind: 'tool';  tool: 'axe' | 'pickaxe' | 'field' | 'water' | 'plant' | 'scythe' }
  | { kind: 'build' };

type Slot = {
  id: string;
  label: string;
  icon: string;
  action: SlotKind;
  visible: (s: Sim) => boolean;
};

/** Each tool inside the Gardening popover. */
const GARDEN_TOOLS: { tool: ToolId; label: string; icon: string; unlocked: (s: Sim) => boolean }[] = [
  { tool: 'field',  label: 'Hoe',    icon: ICON.hoe,    unlocked: s => !!s.state.prog.unlocked.field },
  { tool: 'water',  label: 'Water',  icon: ICON.water,  unlocked: s => !!s.state.prog.unlocked.water },
  { tool: 'plant',  label: 'Plant',  icon: ICON.plant,  unlocked: s => !!s.state.prog.unlocked.plant },
  { tool: 'scythe', label: 'Sickle', icon: ICON.scythe, unlocked: s => !!s.state.prog.unlocked.scythe },
];

export class Hotbar {
  private root: HTMLDivElement;
  /** Primary popover — gardening tools row OR build cards. */
  private popover: HTMLDivElement;
  /** Secondary popover — seed picker, stacked above the gardening popover
   *  when Plant is the active gardening tool. */
  private seedsPopover: HTMLDivElement;
  /** What's currently open above the bar. null = hidden. */
  private openMode: 'gardening' | 'build' | null = null;
  private lastHash = '';

  constructor(parent: HTMLElement, private sim: Sim) {
    this.root = document.createElement('div');
    this.root.className = 'ui-hotbar';
    parent.appendChild(this.root);
    this.popover = document.createElement('div');
    this.popover.className = 'ui-hotpop';
    parent.appendChild(this.popover);
    this.seedsPopover = document.createElement('div');
    this.seedsPopover.className = 'ui-hotpop ui-hotpop--seeds';
    parent.appendChild(this.seedsPopover);
    this.root.addEventListener('click', this.onClick);
    // Single delegated click listener per popover root. Previously the
    // popover code re-added per-button click handlers on every rebuild,
    // which under sim-bus-driven re-renders meant hundreds of orphaned
    // listener bindings per minute. closest('[data-action]') correctly
    // resolves clicks that land on an emoji glyph child.
    this.popover.addEventListener('click', this.onPopoverClick);
    this.seedsPopover.addEventListener('click', this.onPopoverClick);
    this.root.addEventListener('wheel', this.onWheel, { passive: false });
    window.addEventListener('keydown', this.onKey);
    sim.bus.on(() => this.rebuild());
    this.rebuild();
  }

  /** The visible slot set — progressive disclosure trims locked tools.
   *  In plot mode, only gardening-relevant slots remain so the hotbar
   *  matches the work the player can actually do inside the plot. */
  private slots(): Slot[] {
    const everSeenStone = (this.sim.state.res.stone ?? 0) > 0;
    const lv = this.sim.state.prog.level;
    const everSeenWood  = this.sim.state.res.wood > 0;
    const inPlot = !!this.sim.state.plotInView;
    // In plot mode the hotbar is the gardening toolkit itself — the
    // Create-Area + Garden popover slots are replaced by 4 direct tool
    // slots, and woodland/stone/build are hidden.
    if (inPlot) {
      const list: Slot[] = [
        { id: 'field',  label: 'Hoe',    icon: ICON.hoe,    action: { kind: 'tool', tool: 'field' },  visible: s => !!s.state.prog.unlocked.field },
        { id: 'water',  label: 'Water',  icon: ICON.water,  action: { kind: 'tool', tool: 'water' },  visible: s => !!s.state.prog.unlocked.water },
        { id: 'plant',  label: 'Plant',  icon: ICON.plant,  action: { kind: 'tool', tool: 'plant' },  visible: s => !!s.state.prog.unlocked.plant },
        { id: 'scythe', label: 'Sickle', icon: ICON.scythe, action: { kind: 'tool', tool: 'scythe' }, visible: s => !!s.state.prog.unlocked.scythe },
      ];
      return list.filter(s => s.visible(this.sim));
    }
    const list: Slot[] = [
      { id: 'plot',      label: 'Create area', icon: ICON.plot,    action: { kind: 'plot' },                  visible: () => true },
      { id: 'gardening', label: 'Garden',      icon: ICON.wheat,   action: { kind: 'gardening' },             visible: s => GARDEN_TOOLS.some(g => g.unlocked(s)) },
      { id: 'axe',       label: 'Axe',         icon: ICON.axe,     action: { kind: 'tool', tool: 'axe' },     visible: s => !!s.state.prog.unlocked.axe },
      { id: 'pickaxe',   label: 'Pickaxe',     icon: ICON.pickaxe, action: { kind: 'tool', tool: 'pickaxe' }, visible: s => !!s.state.prog.unlocked.pickaxe && (everSeenStone || lv >= 2) },
      { id: 'build',     label: 'Build',       icon: ICON.build,   action: { kind: 'build' },                 visible: s => everSeenWood || s.state.prog.level >= 2 },
    ];
    return list.filter(s => s.visible(this.sim));
  }

  private rebuild() {
    const visible = this.slots();
    const sel = this.sim.state.tool;
    const inPlot = !!this.sim.state.plotInView || !!this.sim.state.selecting;
    const placing = this.sim.state.placing?.type ?? '';
    const special = this.sim.state.selectedSpecialSeed ?? '';
    const seedHash = `${this.sim.state.selectedSeed}|${special}`;
    const r = this.sim.state.res;
    const resHash = `${r.seeds.wheat},${r.seeds.corn},${r.seeds.carrot}|${r.berries ?? 0}|${r.treeSeeds ?? 0}|${r.money}|${r.wood}|${r.basket}|${r.stone ?? 0}`;
    const hash =
      visible.map(s => s.id).join('|') + '|' + sel + '|' + (inPlot ? 1 : 0) + '|' +
      placing + '|' + this.openMode + '|' + seedHash + '|' + resHash;
    if (hash === this.lastHash) return;
    this.lastHash = hash;
    this.root.innerHTML = visible.map((s, i) => {
      const active = this.isSlotActive(s);
      const digit = i + 1;
      return `
        <button type="button" class="ui-hotbar__slot ${active ? 'active' : ''}" data-slot-id="${s.id}" title="${s.label} (${digit})">
          <span class="ui-hotbar__digit">${digit}</span>
          <span class="ui-hotbar__icon glyph">${s.icon}</span>
          <span class="ui-hotbar__label">${s.label}</span>
        </button>
      `;
    }).join('');
    this.updatePopover();
  }

  /** Is this slot the "active" one (shown highlighted)? */
  private isSlotActive(s: Slot): boolean {
    const sim = this.sim;
    const sel = sim.state.tool;
    switch (s.action.kind) {
      case 'plot':      return !!sim.state.plotInView || !!sim.state.selecting || !!sim.state.zonesMode;
      case 'gardening': return GARDEN_TOOLS.some(g => g.tool === sel) || this.openMode === 'gardening';
      case 'tool':      return sel === s.action.tool;
      case 'build':     return !!sim.state.placing || this.openMode === 'build';
    }
  }

  private onClick = (e: Event) => {
    const btn = (e.target as HTMLElement).closest('.ui-hotbar__slot') as HTMLButtonElement | null;
    if (!btn || btn.disabled) return;
    // Same as the popover: stop the doc-level outside-click listener.
    e.stopPropagation();
    const id = btn.dataset.slotId!;
    const slot = this.slots().find(s => s.id === id);
    if (!slot) return;
    this.activate(slot);
  };

  private activate(slot: Slot) {
    const sim = this.sim;
    // Helper: force-close whatever popover is open AND force a re-render
    // even if the sim emits nothing (e.g. clicking Pickaxe twice while
    // Build was open — setTool('hands') fires no event because tool was
    // already hands, so we have to nudge the rebuild ourselves).
    const closePopover = () => {
      if (this.openMode !== null) {
        this.openMode = null;
        this.lastHash = '';   // invalidate the hash so the next rebuild
        this.rebuild();       // re-runs even with no sim event
      }
    };
    switch (slot.action.kind) {
      case 'plot':
        // The plot slot is the entry point to the Zones manager. From
        // there the player creates / edits / dives into plots. While
        // already inside a plot, the slot toggles back to overview.
        if (sim.state.plotInView)        sim.exitPlot();
        else if (sim.state.zonesMode)    sim.exitZonesMode();
        else                              sim.enterZonesMode();
        closePopover();
        return;
      case 'gardening':
        // Toggle the popover. The Garden slot is just a portal to the
        // tool picker — it must NOT auto-select a tool, otherwise just
        // opening the popover leaves the player with "hoe selected" they
        // never asked for and the slot looks stuck in the active state.
        if (this.openMode === 'gardening') {
          this.openMode = null;
        } else {
          this.openMode = 'gardening';
        }
        sim.cancelPlacement();
        this.lastHash = '';
        this.rebuild();
        return;
      case 'tool': {
        const t = slot.action.tool;
        if (sim.state.tool === t) sim.setTool('hands');
        else                       sim.setTool(t);
        sim.cancelPlacement();
        closePopover();
        return;
      }
      case 'build':
        // Toggle the build popover. Cancels any in-flight placement so
        // the player can pick a different building.
        if (this.openMode === 'build') {
          this.openMode = null;
          sim.cancelPlacement();
        } else {
          this.openMode = 'build';
          sim.setTool('hands');
        }
        this.lastHash = '';
        this.rebuild();
        return;
    }
  }

  private updatePopover() {
    const inPlot = !!this.sim.state.plotInView;
    const plantActive = this.sim.state.tool === 'plant';
    // Seed picker visibility: open when plant is the active tool in plot
    // mode (no gardening popover to live under), OR when the gardening
    // popover is open with plant selected.
    const showSeedsPlotMode = inPlot && plantActive;
    const showSeedsGarden   = this.openMode === 'gardening' && plantActive;
    if (this.openMode === 'gardening' && !inPlot) {
      this.renderGardeningPopover();
    } else if (this.openMode === 'build' && !inPlot) {
      this.renderBuildPopover();
    } else {
      this.popover.classList.remove('open');
      this.popover.innerHTML = '';
    }
    if (showSeedsGarden) {
      this.renderSeedsPopover();
    } else if (showSeedsPlotMode) {
      this.renderSeedsPopoverAnchored('plant');
    } else {
      this.seedsPopover.classList.remove('open');
      this.seedsPopover.innerHTML = '';
    }
  }

  /** Plot-mode variant — render the seeds popover and anchor it above
   *  a specific hotbar slot (the 'plant' tool) instead of above the
   *  gardening popover (which isn't open in plot mode). */
  private renderSeedsPopoverAnchored(slotId: string) {
    this.renderSeedsPopover();
    // renderSeedsPopover anchored above `this.popover` (the gardening
    // one), which is closed in plot mode → 0×0 rect, breaks layout.
    // Re-anchor against the hotbar slot directly.
    this.anchorToSlot(this.seedsPopover, slotId);
  }

  private renderGardeningPopover() {
    const sim = this.sim;
    const sel = sim.state.tool;
    // Per-tool background tint so the 4 garden slots are visually
    // distinct at rest (without this they all read identical because
    // they share button styling — discovery friction reported in QA).
    // Tints are subtle so the active state still pops via the .active
    // class. Tones match the tool's role: brown=soil, blue=water,
    // green=plant, gold=harvest.
    const TINT: Record<string, string> = {
      field:  'rgba(160, 116, 64,  0.22)',   // hoe — earth-brown
      water:  'rgba(80,  152, 224, 0.22)',   // watering can — water-blue
      plant:  'rgba(122, 212, 64,  0.22)',   // plant — leaf-green
      scythe: 'rgba(232, 200, 64,  0.22)',   // sickle — wheat-gold
    };
    const toolsHTML = GARDEN_TOOLS.filter(g => g.unlocked(sim)).map(g => {
      const active = sel === g.tool;
      const bg = TINT[g.tool] ?? '';
      return `
        <button type="button" class="ui-hotpop__slot ${active ? 'active' : ''}" data-action="tool" data-tool="${g.tool}" title="${g.label}" style="${bg ? `background:${bg};` : ''}">
          <span class="ui-hotpop__icon glyph">${g.icon}</span>
          <span class="ui-hotpop__label">${g.label}</span>
        </button>
      `;
    }).join('');
    this.popover.innerHTML = `
      <div class="ui-hotpop__title"><span class="glyph glyph-inline">${ICON.wheat}</span> Gardening</div>
      <div class="ui-hotpop__row">${toolsHTML}</div>
    `;
    this.popover.classList.add('open');
    this.attachPopoverListeners(this.popover);
    this.anchorToSlot(this.popover, 'gardening');
  }

  private renderSeedsPopover() {
    const sim = this.sim;
    const r = sim.state.res;
    const lv = sim.state.prog.level;
    const special = sim.state.selectedSpecialSeed ?? null;
    const selSeed = sim.state.selectedSeed;
    const seedGlyph: Record<SeedType, string> = { wheat: ICON.wheat, corn: ICON.corn, carrot: ICON.carrot };
    const cropHTML = SEED_TYPES.map(seed => {
      const locked = seed !== 'wheat' && lv < 2;
      const active = selSeed === seed && special === null;
      const count = r.seeds[seed] ?? 0;
      return `
        <button type="button" class="ui-hotpop__slot ${active ? 'active' : ''} ${locked ? 'locked' : ''}" data-action="seed" data-seed="${seed}" ${locked ? 'disabled' : ''} title="${seed} ×${count}">
          <span class="ui-hotpop__icon glyph">${seedGlyph[seed]}</span>
          <span class="ui-hotpop__count">${locked ? '🔒' : count}</span>
        </button>
      `;
    }).join('');
    const specialHTML = [
      { id: 'berry' as const, icon: ICON.berry, count: r.berries ?? 0,    label: 'Berry' },
      { id: 'acorn' as const, icon: ICON.acorn, count: r.treeSeeds ?? 0, label: 'Acorn' },
    ].map(s => {
      const empty = s.count <= 0;
      const active = special === s.id;
      return `
        <button type="button" class="ui-hotpop__slot ${active ? 'active' : ''} ${empty ? 'locked' : ''}" data-action="special" data-special="${s.id}" ${empty ? 'disabled' : ''} title="${s.label} ×${s.count}">
          <span class="ui-hotpop__icon glyph">${s.icon}</span>
          <span class="ui-hotpop__count">${s.count}</span>
        </button>
      `;
    }).join('');
    this.seedsPopover.innerHTML = `
      <div class="ui-hotpop__title"><span class="glyph glyph-inline">${ICON.seed}</span> Seeds</div>
      <div class="ui-hotpop__row">${cropHTML}${specialHTML}</div>
    `;
    this.seedsPopover.classList.add('open');
    this.attachPopoverListeners(this.seedsPopover);
    this.anchorAbove(this.seedsPopover, this.popover);
  }

  private renderBuildPopover() {
    const sim = this.sim;
    const res = sim.state.res;
    const buildings: { id: BuildingType; label: string; icon: string; sub: string }[] = [
      { id: 'granary', label: 'Granary',  icon: ICON.granary, sub: '+50 storage · 2 stages' },
      { id: 'mill',    label: 'Windmill', icon: ICON.mill,    sub: '5 grain → flour · 3 stages' },
    ];
    const cardsHTML = buildings.map(b => {
      const stages = BUILDING_STAGES[b.id];
      const stage1 = stages[0] ?? { money: 0, wood: 0, wheat: 0, stone: 0 };
      const total  = totalBuildingCost(b.id);
      const need = (k: 'money' | 'wood' | 'wheat' | 'stone') => (stage1 as any)[k] ?? 0;
      const aff =
        res.money  >= need('money') &&
        res.wood   >= need('wood')  &&
        res.basket >= need('wheat') &&
        (res.stone ?? 0) >= need('stone');
      const placingThis = sim.state.placing?.type === b.id;
      const fmt = (c: Record<string, number | undefined>) => {
        const parts: string[] = [];
        if (c.money) parts.push(`${c.money}<span class="glyph glyph-inline">${ICON.coin}</span>`);
        if (c.wood)  parts.push(`${c.wood}<span class="glyph glyph-inline">${ICON.wood}</span>`);
        if (c.stone) parts.push(`${c.stone}<span class="glyph glyph-inline">${ICON.stone}</span>`);
        if (c.wheat) parts.push(`${c.wheat}<span class="glyph glyph-inline">${ICON.wheat}</span>`);
        return parts.join(' · ') || '—';
      };
      return `
        <div class="ui-buildpop__card ${aff ? '' : 'locked'} ${placingThis ? 'active' : ''}">
          <div class="ui-buildpop__head">
            <span class="ui-buildpop__icon glyph">${b.icon}</span>
            <span class="ui-buildpop__name">${b.label}</span>
          </div>
          <div class="ui-buildpop__sub">${b.sub}</div>
          <div class="ui-buildpop__cost">Stage 1: ${fmt(stage1 as any)}</div>
          <div class="ui-buildpop__cost ui-buildpop__cost--total">Total: ${fmt(total as any)}</div>
          <button type="button" class="ui-btn ui-btn--primary ui-btn--block" data-action="build" data-building="${b.id}" ${aff ? '' : 'disabled'}>
            ${placingThis ? 'Placing… (Esc cancels)' : 'Build this'}
          </button>
        </div>
      `;
    }).join('');
    this.popover.innerHTML = `
      <div class="ui-hotpop__title"><span class="glyph glyph-inline">${ICON.build}</span> Build</div>
      <div class="ui-buildpop__grid">${cardsHTML}</div>
      <div class="ui-hotpop__tip">Placement pays stage 1 upfront. Click the site to fund each next stage as you go.</div>
    `;
    this.popover.classList.add('open');
    this.attachPopoverListeners(this.popover);
    this.anchorToSlot(this.popover, 'build');
  }

  /** Legacy no-op. Per-button listeners were replaced with a single
   *  delegated listener on each popover root in the constructor; this
   *  shim exists so the render code's call sites stay terse. */
  private attachPopoverListeners(_pop: HTMLDivElement) { /* no-op */ }


  /** Anchor a popover horizontally above the named slot. The popover
   *  MUST already be visible (`.open`) so getBoundingClientRect can
   *  measure its rendered width. We position it with an absolute `left`
   *  instead of `transform: translateX(-50%)` — transforms on fixed
   *  elements occasionally produce sub-pixel hit-test offsets that
   *  manifest as "only the borders click". */
  private anchorToSlot(pop: HTMLDivElement, slotId: string) {
    const btn = this.root.querySelector(`[data-slot-id="${slotId}"]`) as HTMLElement | null;
    if (!btn) return;
    const slotRect = btn.getBoundingClientRect();
    const popRect = pop.getBoundingClientRect();
    const centerX = slotRect.left + slotRect.width / 2;
    let left = Math.round(centerX - popRect.width / 2);
    // Clamp horizontally so the popover stays on-screen.
    left = Math.max(8, Math.min(window.innerWidth - popRect.width - 8, left));
    pop.style.left = `${left}px`;
    pop.style.bottom = `${Math.round(window.innerHeight - slotRect.top + 8)}px`;
  }

  /** Stack `pop` directly above another popover (used for Seeds-above-Gardening). */
  private anchorAbove(pop: HTMLDivElement, anchor: HTMLDivElement) {
    const anchorRect = anchor.getBoundingClientRect();
    const popRect = pop.getBoundingClientRect();
    const centerX = anchorRect.left + anchorRect.width / 2;
    let left = Math.round(centerX - popRect.width / 2);
    left = Math.max(8, Math.min(window.innerWidth - popRect.width - 8, left));
    pop.style.left = `${left}px`;
    pop.style.bottom = `${Math.round(window.innerHeight - anchorRect.top + 8)}px`;
  }

  private onPopoverClick = (e: Event) => {
    const t = e.target as HTMLElement;
    const btn = t.closest('[data-action]') as HTMLButtonElement | null;
    if (!btn || btn.disabled) return;
    const action = btn.dataset.action;
    if (action === 'tool') {
      const tool = btn.dataset.tool as ToolId;
      this.sim.setTool(tool);
      // Force rebuild so the seed picker appears immediately when Plant
      // becomes active (setTool emits stateChanged but we want to make
      // sure the popover stack updates even on no-op tool clicks).
      this.lastHash = '';
      this.rebuild();
      return;
    }
    if (action === 'seed') {
      const seed = btn.dataset.seed as SeedType;
      this.sim.setSelectedSeed(seed);
      if (this.sim.state.tool !== 'plant') this.sim.setTool('plant');
      this.lastHash = '';
      this.rebuild();
      return;
    }
    if (action === 'special') {
      const kind = btn.dataset.special as 'berry' | 'acorn';
      const cur = this.sim.state.selectedSpecialSeed;
      this.sim.setSelectedSpecialSeed(cur === kind ? null : kind);
      if (this.sim.state.tool !== 'plant') this.sim.setTool('plant');
      this.lastHash = '';
      this.rebuild();
      return;
    }
    if (action === 'build') {
      const type = btn.dataset.building as BuildingType;
      this.sim.selectBuilding(type);
      this.sim.enterPlacementMode();
      this.openMode = null;
      this.lastHash = '';
      this.rebuild();
      return;
    }
  };

  private selectAt(i: number) {
    const visible = this.slots();
    if (i < 0 || i >= visible.length) return;
    this.activate(visible[i]);
  }

  private onWheel = (e: WheelEvent) => {
    e.preventDefault();
    const visible = this.slots();
    if (visible.length === 0) return;
    const sel = this.sim.state.tool;
    let i = visible.findIndex(s => {
      if (s.action.kind === 'tool')      return sel === s.action.tool;
      if (s.action.kind === 'gardening') return GARDEN_TOOLS.some(g => g.tool === sel);
      return false;
    });
    if (i < 0) i = 0;
    const delta = e.deltaY > 0 ? +1 : -1;
    const next = (i + delta + visible.length) % visible.length;
    this.activate(visible[next]);
  };

  private onKey = (e: KeyboardEvent) => {
    const target = e.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
    if (e.key === 'Escape' && this.openMode) {
      this.openMode = null;
      this.lastHash = '';
      this.rebuild();
      return;
    }
    const digit = parseInt(e.key, 10);
    if (!Number.isNaN(digit) && digit >= 1 && digit <= 9) {
      e.preventDefault();
      this.selectAt(digit - 1);
    }
  };
}
